import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useAuth, apiErrorMessage } from '~/lib/auth'
import { AppHeader } from '~/components/app-header'
import { GoogleAuthButton } from '~/components/google-auth-button'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Notice } from '~/components/ui/notice'

export const Route = createFileRoute('/login')({
  head: () => ({
    meta: [
      { title: 'Sign in — Provenance' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: LoginPage,
})

function LoginPage() {
  const { user, loading, login, forgotPassword } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!loading && user) navigate({ to: '/dpp' })
  }, [loading, user, navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    setSubmitting(true)
    try {
      await login(email, password)
      navigate({ to: '/dpp' })
    } catch (err) {
      setError(apiErrorMessage(err, 'Sign in failed'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleForgot() {
    setError('')
    setNotice('')
    if (!email) {
      setError('Enter your email above, then select reset.')
      return
    }
    try {
      await forgotPassword(email)
      setNotice('If that email exists, a reset code is on its way.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send reset email'))
    }
  }

  return (
    <>
      <AppHeader />
      <main className="mx-auto flex min-h-[calc(100vh-64px)] max-w-[1320px] items-center justify-center px-[var(--gutter)] py-16">
        <div className="bracket-frame relative w-full max-w-[440px] border border-[var(--line)] bg-surface p-8 sm:p-10">
          <span className="bk-bl" aria-hidden="true" />
          <span className="bk-br" aria-hidden="true" />

          <div className="mb-2 font-mono text-[11px] tracking-[0.16em] uppercase text-accent">
            Provenance · Account
          </div>
          <h1 className="mb-8 font-mono text-[28px] font-[500] leading-[1.1] text-ink">
            Sign in
          </h1>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={handleForgot}
                  className="mb-2 font-mono text-[10px] tracking-[0.12em] uppercase text-ink-mute transition-colors hover:text-accent"
                >
                  Forgot?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••••"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>

            {error && <Notice>{error}</Notice>}
            {notice && <Notice tone="success">{notice}</Notice>}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-6">
            <GoogleAuthButton onError={setError} />
          </div>

          <p className="mt-8 border-t border-[var(--line)] pt-6 text-center font-mono text-[11px] tracking-[0.06em] text-ink-mute">
            No account yet?{' '}
            <Link to="/register" className="text-accent hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </main>
    </>
  )
}
